/**
 * Returns a safe representation of an error for console logging.
 * Strips details, hint, and stack to avoid leaking DB/internal info in logs
 * (e.g. in support screenshots or devtools).
 * Use this whenever logging Supabase or other backend errors in the client.
 *
 * @param {Error|object|null} error
 * @returns {object} Safe object with at most message, code, status
 */
export function sanitizeErrorForLog(error) {
  if (error == null) return null;
  const safe = {};
  if (typeof error.message === 'string') safe.message = error.message;
  if (error.code != null) safe.code = error.code;
  if (error.status != null) safe.status = error.status;
  return Object.keys(safe).length ? safe : { message: String(error) };
}

/** Generic message for UI when we must not expose backend/DB error details. */
const GENERIC_USER_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Returns a safe, user-facing error message. Use for alerts, setError, setMessage, etc.
 * Never pass through error.message from Supabase/DB to the UI (can leak table names, constraints, paths).
 *
 * @param {Error|object|null} error
 * @param {string} [fallback] Optional fallback (e.g. "Failed to save."). Defaults to generic message.
 * @returns {string}
 */
export function getUserFacingErrorMessage(error, fallback = GENERIC_USER_MESSAGE) {
  return fallback || GENERIC_USER_MESSAGE;
}
